import {
  LinhaComposicao,
  ComposicaoProposta,
  TipoLinha,
  ADICIONAIS_TV,
  ADICIONAIS_FIBRA,
  getAdicionalMovelById,
  getPlanoById,
  categoriaDoTipo,
  PlanoCNPJ,
  linhaExigeTipoChip,
  linhaTemChip,
} from './planos-cnpj.js';
import { megaBonusGb } from '../whatsapp/gerar-mensagem-proposta.js';
type Json = any;
type FamiliaProdutoEnum = string;
type FaturamentoTipoEnum = string;
import { normalizarMsisdn } from './normalizar-msisdn.js';
import { linhaPertenceAItem, type ItemPedidoMatch } from './linhas-protegidas.js';
import { CATEGORIA_M2M } from './servico-na-linha.js';


export interface ConverterPropostaParams {
  pedidoId: string;
  composicao: ComposicaoProposta;
  gestorId?: string | null;
  cessionarioId?: string | null;
}

export interface ConverterPropostaResult {
  success: boolean;
  itensCreados: number;
  errors: string[];
}

/**
 * Converter proposta em itens do pedido
 */

export function criarConversorProposta(supabase: any, toast: { error: (...args: any[]) => void; warning: (...args: any[]) => void }) {
interface Mapeamento {
  tipo_proposta: string;
  produto_id: string;
  descricao: string;
}

interface LinhaItemJson {
  [key: string]: string | null;
}

interface StatusInicial {
  id: string;
  coluna: string;
  quadro: string;
}

interface Produto {
  id: string;
  familia: FamiliaProdutoEnum;
  faturamento_tipo: FaturamentoTipoEnum;
}

interface AdicionalMovelPedido {
  id: string;
  nome: string;
  valor: number;
  gb: number | null;
  produtoId: string;
}

interface RenovacaoSnapshotLinha {
  msisdn: string;
  plano_atual: string | null;
  gb_atual: number;
  gb_minimo: number;
  plano_novo: string | null;
  gb_novo: number | null;
  ticket_atual: number;
  ticket_novo: number;
  receita_renovada: number;
  receita_adicionada: number;
  delta_economico: number | null;
  delta_literal: number | null;
  faixa_apurada: number | null;
  tabela_apurada: '1' | '2' | 'INDETERMINADO';
}

interface RenovacaoSnapshotPayload {
  deltaFormulaSign?: 'ECONOMICO' | 'LITERAL_REGULAMENTO';
  linhas?: RenovacaoSnapshotLinha[];
}

// Cache de mapeamentos e status (não persistente entre sessões — sempre refetch se vazio)
let mapeamentosCache: Mapeamento[] | null = null;
let statusInicialCache: StatusInicial | null = null;
let produtosCache: Map<string, Produto> = new Map();
let produtoPorCategoriaCache: Map<string, string | null> = new Map();

async function resolverAdicionaisMovel(
  ids: string[],
): Promise<{ adicionais: Map<string, AdicionalMovelPedido>; errors: string[] }> {
  const unicos = Array.from(new Set(ids));
  const adicionais = new Map<string, AdicionalMovelPedido>();
  const errors: string[] = [];
  if (unicos.length === 0) return { adicionais, errors };

  const { data, error } = await supabase
    .from('planos_proposta_catalogo' as never)
    .select('codigo,nome,valor,gb,metadata,produto_pedido_id')
    .eq('categoria', 'adicional_movel')
    .in('codigo', unicos);

  if (error) {
    return {
      adicionais,
      errors: [`Não foi possível conferir os Passaports da proposta: ${error.message}`],
    };
  }

  const porCodigo = new Map(
    ((data ?? []) as Array<{
      codigo: string;
      nome: string;
      valor: number | string;
      gb: number | null;
      metadata: Record<string, unknown> | null;
      produto_pedido_id: string | null;
    }>).map((row) => [row.codigo, row]),
  );

  for (const id of unicos) {
    const row = porCodigo.get(id);
    const fallback = getAdicionalMovelById(id);
    const metadataProdutoId = typeof row?.metadata?.produtoId === 'string'
      ? row.metadata.produtoId
      : null;
    const produtoId = row?.produto_pedido_id ?? metadataProdutoId ?? fallback?.produtoId ?? null;
    if (!row && !fallback) {
      errors.push(`Passaport ${id} não existe mais no catálogo`);
      continue;
    }
    if (!produtoId) {
      errors.push(`Passaport ${row?.nome ?? fallback?.nome ?? id} está sem vínculo com o produto do pedido`);
      continue;
    }
    adicionais.set(id, {
      id,
      nome: row?.nome ?? fallback!.nome,
      valor: Number(row?.valor ?? fallback!.valor),
      gb: row?.gb ?? fallback?.gb ?? null,
      produtoId,
    });
  }

  return { adicionais, errors };
}

/** Limpar caches para forçar novo fetch (use ao re-tentar após erro) */
function invalidarCacheConversor() {
  mapeamentosCache = null;
  statusInicialCache = null;
  produtosCache = new Map();
  produtoPorCategoriaCache = new Map();
}

/**
 * Buscar mapeamentos de tipo_proposta -> produto_id
 */
async function buscarMapeamentos(): Promise<Mapeamento[]> {
  if (mapeamentosCache) return mapeamentosCache;

  const { data, error } = await supabase
    .from('proposta_produto_mapeamento')
    .select('tipo_proposta, produto_id, descricao')
    .eq('ativo', true);

  if (error) {
    console.error('[useConverterPropostaEmItens] Erro ao buscar mapeamentos:', error);
    toast.error('Configuração de produtos indisponível', {
      description: 'Não foi possível carregar o mapeamento de produtos. Tente novamente.',
    });
    return [];
  }

  if (data && data.length > 0) {
    mapeamentosCache = data;
  } else if (!mapeamentosCache) {
    toast.warning('Mapeamento de produtos vazio', {
      description: 'Nenhuma configuração de produto foi encontrada. Verifique a tabela proposta_produto_mapeamento.',
    });
  }
  return mapeamentosCache ?? [];
}

/**
 * Buscar status inicial "AGUARDANDO INTERAÇÃO" no COMERCIAL
 */
async function buscarStatusInicial(): Promise<StatusInicial | null> {
  if (statusInicialCache) return statusInicialCache;

  const { data, error } = await supabase
    .from('status_catalogo')
    .select('id, coluna, quadro')
    .eq('nome', 'AGUARDANDO INTERAÇÃO')
    .eq('quadro', 'COMERCIAL')
    .eq('ativo', true)
    .single();

  if (error) {
    console.error('[useConverterPropostaEmItens] Erro ao buscar status inicial:', error);
    toast.error('Status inicial não encontrado', {
      description: 'Não foi possível carregar o status "AGUARDANDO INTERAÇÃO" do quadro COMERCIAL. Verifique as configurações.',
    });
    return null;
  }

  statusInicialCache = data;
  return statusInicialCache;
}

/**
 * Buscar dados do produto (família, faturamento)
 */
async function buscarProduto(produtoId: string): Promise<Produto | null> {
  if (produtosCache.has(produtoId)) {
    return produtosCache.get(produtoId)!;
  }

  const { data, error } = await supabase
    .from('produtos')
    .select('id, familia, faturamento_tipo')
    .eq('id', produtoId)
    .single();

  if (error) {
    console.error('[useConverterPropostaEmItens] Erro ao buscar produto:', error);
    return null;
  }

  produtosCache.set(produtoId, data);
  return data;
}

/**
 * Produto do pedido de uma linha de categoria custom (criada pelo admin):
 * 1º o produto_pedido_id da OPÇÃO (planos_proposta_catalogo), 2º o default
 * do TIPO (proposta_tipos). Usado quando a linha não trouxe o plano hidratado
 * com produtoPedidoId (propostas salvas antes do fix, hidratação parcial).
 */
async function buscarProdutoPorCategoria(categoria: string, codigo?: string | null): Promise<string | null> {
  const key = `${categoria}::${codigo ?? ''}`;
  if (produtoPorCategoriaCache.has(key)) return produtoPorCategoriaCache.get(key)!;

  let produtoId: string | null = null;
  if (codigo) {
    const { data } = await supabase
      .from('planos_proposta_catalogo' as any)
      .select('produto_pedido_id')
      .eq('categoria', categoria)
      .eq('codigo', codigo)
      .maybeSingle();
    produtoId = (data as any)?.produto_pedido_id ?? null;
  }
  if (!produtoId) {
    const { data } = await supabase
      .from('proposta_tipos' as any)
      .select('produto_pedido_id')
      .eq('categoria', categoria)
      .maybeSingle();
    produtoId = (data as any)?.produto_pedido_id ?? null;
  }
  produtoPorCategoriaCache.set(key, produtoId);
  return produtoId;
}

/**
 * Última tentativa: resolve o produto pelo CÓDIGO do plano, sem saber a categoria.
 *
 * (Douglas 05/08/2026) Existe porque a composição salva no lead vinha SEM
 * `categoriaCustom` e SEM o objeto `plano` — só `{tipo:'SERVICO', planoId}`. Sem
 * os dois, o ramo de serviço não tinha por onde resolver e morria com
 * "Produto do pedido não configurado", mandando arrumar um cadastro que estava
 * CERTO. Resultado: **nenhum item de família TI jamais entrou no sistema** —
 * zero Claro Monitor, McAfee, M365 ou Workspace.
 *
 * ⚠️ Só serve quando o código é único. `codigo` NÃO é único no catálogo (127
 * distintos em 183 linhas — `pos-70`, `port-25` etc. se repetem entre
 * categorias, e é por isso que o caminho principal passa a categoria). Os de
 * serviço digital trazem a categoria no próprio nome
 * (`servico_digital-claro_monitor`) e são únicos — conferido: 0 duplicados
 * entre os 17. Por segurança a busca exige unicidade: 2+ linhas com o mesmo
 * código devolve null em vez de chutar a primeira.
 */
async function buscarProdutoPorCodigoUnico(codigo: string): Promise<string | null> {
  const key = `codigo::${codigo}`;
  if (produtoPorCategoriaCache.has(key)) return produtoPorCategoriaCache.get(key)!;

  const { data } = await supabase
    .from('planos_proposta_catalogo' as never)
    .select('produto_pedido_id')
    .eq('codigo', codigo)
    .limit(2);

  const linhas = (data || []) as unknown as { produto_pedido_id?: string | null }[];
  const produtoId = linhas.length === 1 ? (linhas[0]?.produto_pedido_id ?? null) : null;
  produtoPorCategoriaCache.set(key, produtoId);
  return produtoId;
}

/**
 * Buscar próximo número sequencial do pedido
 */
async function buscarProximoSeq(pedidoId: string): Promise<number> {
  const { data } = await supabase
    .from('itens_pedido')
    .select('seq')
    .eq('pedido_id', pedidoId)
    .order('seq', { ascending: false })
    .limit(1);

  return data && data.length > 0 ? (data[0].seq || 0) + 1 : 1;
}

/**
 * Resolver produto_id pelo tipo da proposta
 */
async function resolverProdutoId(
  tipo: TipoLinha,
  tipoFibra?: 'CNPJ' | 'TT',
  tipoBloco?: 'NOVO' | 'INCREMENTO' | 'RENOVACAO'
): Promise<string | null> {
  const mapeamentos = await buscarMapeamentos();

  // Para PORTABILIDADE, sempre usar PN NOVO (PORTABILIDADE_PF)
  if (tipo === 'PORTABILIDADE') {
    const mapeamento = mapeamentos.find(m => m.tipo_proposta === 'PORTABILIDADE_PF');
    return mapeamento?.produto_id || null;
  }

  // Para FIBRA, usar tipoFibra para escolher FIBRA ou FIBRA_TT
  if (tipo === 'FIBRA') {
    const tipoMapeamento = tipoFibra === 'TT' ? 'FIBRA_TT' : 'FIBRA';
    const mapeamento = mapeamentos.find(m => m.tipo_proposta === tipoMapeamento);
    return mapeamento?.produto_id || null;
  }

  // Para NOVA (Bloco/HA), usar tipoBloco para escolher entre HA NOVO / HA INCREMENTO / RENOVAÇAO
  if (tipo === 'NOVA') {
    let tipoMapeamento = 'NOVA';
    if (tipoBloco === 'INCREMENTO') tipoMapeamento = 'NOVA_INCREMENTO';
    else if (tipoBloco === 'RENOVACAO') tipoMapeamento = 'NOVA_RENOVACAO';
    const mapeamento = mapeamentos.find(m => m.tipo_proposta === tipoMapeamento);
    if (!mapeamento && tipoMapeamento !== 'NOVA') {
      console.warn(`[resolverProdutoId] ${tipoMapeamento} não encontrado, usando NOVA (HA NOVO) como fallback`);
      const fallback = mapeamentos.find(m => m.tipo_proposta === 'NOVA');
      return fallback?.produto_id || null;
    }
    return mapeamento?.produto_id || null;
  }

  // Para outros tipos, buscar diretamente
  const mapeamento = mapeamentos.find(m => m.tipo_proposta === tipo);
  return mapeamento?.produto_id || null;
}

/**
 * Extrair DDD e linha de um número de telefone
 */
function extrairDDDLinha(numero: string): { ddd: string; linha: string } {
  if (!numero) return { ddd: '', linha: '' };

  const digitos = normalizarMsisdn(numero);

  if (digitos.length >= 10) {
    return {
      ddd: digitos.slice(0, 2),
      linha: digitos.slice(2),
    };
  }

  return { ddd: '', linha: digitos };
}

// Passaporte é um módulo, não uma segunda ativação, mas precisa carregar o
// MSISDN da linha principal para continuar identificável no pedido e no extrato.
function camposLinhaPassaporte(numero: string) {
  const { ddd, linha } = extrairDDDLinha(numero);
  return { ddd, linha, linhas_telefone: [{ ddd, linha }] };
}

/**
 * Formatar adicionais para observações do item
 */
function formatarAdicionais(adicionais?: Record<string, number>, incluirEsim: boolean = false): string {
  const partes: string[] = [];

  // Sempre incluir ESIM como padrão quando solicitado
  if (incluirEsim) {
    partes.push('ESIM COMO PADRAO!');
  }

  if (adicionais && Object.keys(adicionais).length > 0) {
    Object.entries(adicionais).forEach(([adicId, quantidade]) => {
      if (quantidade <= 0) return;

      // Buscar nome do adicional
      const adicTV = ADICIONAIS_TV.find(a => a.id === adicId);
      const adicFibra = ADICIONAIS_FIBRA.find(a => a.id === adicId);
      const nome = adicTV?.nome || adicFibra?.nome || adicId;

      partes.push(`${nome}: ${quantidade}x`);
    });
  }

  return partes.join(' | ');
}

/**
 * Formatar observação completa para item FIBRA (velocidade + megabônus + adicionais)
 */
function formatarObservacaoFibra(
  planoFibra: PlanoCNPJ | null,
  adicionais?: Record<string, number>,
  temMovel: boolean = true // Se tem linhas móveis, inclui Megabônus
): string {
  const partes: string[] = ['ESIM COMO PADRAO!'];

  // 1. Velocidade da fibra
  if (planoFibra?.velocidade) {
    partes.push(`Fibra ${planoFibra.velocidade}`);
  }

  // 2. Megabônus quando combo fibra + móvel
  if (temMovel) {
    partes.push(`+${megaBonusGb()}GB Megabônus`);
  }

  // 3. Adicionais (Claro Fixo, Mesh, etc.) - sem duplicar ESIM
  const adicionaisTexto = formatarAdicionais(adicionais, false);
  if (adicionaisTexto) {
    partes.push(adicionaisTexto);
  }

  return partes.join(' | ');
}

/**
 * CPF: o item do dependente diz de qual titular ele é (Douglas 29/09/2026). É o
 * que o BKO precisa para cadastrar na Claro como DEPENDENTE AVULSO do titular,
 * e não como plano próprio.
 */
function obsDependente(linha: LinhaComposicao, todas: LinhaComposicao[]): string {
  if (!linha.titularLinhaId) return '';
  const titular = todas.find((l) => (l.linha_id || l.id) === linha.titularLinhaId);
  const tel = String(titular?.numero ?? '').replace(/\D/g, '');
  const quem = tel.length >= 10 ? `(${tel.slice(0, 2)}) ${tel.slice(2)}` : 'linha nova';
  const tipo = linha.plano?.dependente === 'incluso' ? 'incluído no plano, sem custo'
    : linha.plano?.dependente === 'dados' ? 'só dados' : 'pago';
  return `Dependente do titular ${quem} · ${titular?.plano?.nome ?? ''} · ${tipo}`;
}

/**
 * Agrupar linhas por planoId + tipoBloco (para HA)
 * Separa Bloco Novo / Incremento / Renovação para gerar produtos diferentes no pedido.
 */
function agruparPorPlano(linhas: LinhaComposicao[]): Record<string, LinhaComposicao[]> {
  const grupos: Record<string, LinhaComposicao[]> = {};

  linhas.forEach(linha => {
    const tipoBloco = linha.tipoBloco || 'NOVO';
    const key = `${linha.planoId || 'sem-plano'}::${tipoBloco}${linha.titularLinhaId ? `::${linha.titularLinhaId}` : ''}`;
    if (!grupos[key]) grupos[key] = [];
    grupos[key].push(linha);
  });

  return grupos;
}

/**
 * Resolver plano a partir de linha (usa cache ou getPlanoById)
 */
function resolverPlano(linha: LinhaComposicao): PlanoCNPJ | null {
  // SEMPRE com a categoria da linha. O codigo do plano NAO e unico: `pos-11`
  // existe em `nova` (HA NOVO) e em `tt_pos` (TT POS-PJ) com o mesmo nome, valor e
  // GB - so o produto muda. Resolver pelo codigo puro fazia uma "Linha Nova" virar
  // TT POS-PJ na revisao (sai de ALTAS/VENDA NOVA, vira PP/MIGRACAO). Ver
  // registrarCatalogoRuntime em constants/planosCNPJ.ts. (Douglas 20/08/2026)
  const categoria = categoriaDoTipo(linha.tipo, linha.tipoBloco, linha.categoriaCustom);

  // Plano ja hidratado pela tela: vale. Se ele veio SEM produto (codigo ambiguo
  // resolvido sem categoria), tenta completar pela categoria da linha.
  if (linha.plano?.nome && linha.plano?.valor !== undefined) {
    if (linha.plano.produtoPedidoId || !linha.planoId) return linha.plano;
    const daCategoria = getPlanoById(linha.planoId, categoria);
    return daCategoria?.produtoPedidoId ? daCategoria : linha.plano;
  }
  if (linha.planoId) return getPlanoById(linha.planoId, categoria) || null;
  return null;
}

/**
 * Itens de APARELHO que faltam no pedido, a partir das linhas da composicao.
 *
 * (Douglas 21/08/2026) Existe separado porque sao DOIS caminhos que precisam
 * dele e so' um tinha: a criacao do pedido (conversor) e a REVISAO
 * ("Salvar e Aplicar ao Pedido"). A revisao faz UPDATE-no-lugar nos itens que ja
 * existem e so' INSERE "linhas novas" — e o aparelho nao e' linha, e um campo
 * DENTRO de uma linha que ja tem item. Resultado: escolher o aparelho numa
 * revisao nunca criava nada, em silencio. Caso: lead L229905 MULTICAO, pedido
 * #P2608210038, iPhone 15 256GB incluido duas vezes e sumido nas duas.
 *
 * E' IDEMPOTENTE: pula a linha que ja tem item de aparelho no pedido (casa pelo
 * telefone gravado em `observacoes_item`, porque o item de aparelho nasce com
 * `linha_id` nulo — ele e vinculado, nao e' a linha).
 */
async function criarItensAparelhoFaltantes(params: {
  pedidoId: string;
  composicao: ComposicaoProposta;
  gestorId?: string | null;
}): Promise<{ criados: number; errors: string[] }> {
  const { pedidoId, composicao, gestorId } = params;
  const errors: string[] = [];
  let criados = 0;

  const comAparelho = (composicao?.linhas || []).filter((l) => l.aparelho);
  if (comAparelho.length === 0) return { criados: 0, errors: [] };

  const { data: prodAparelho } = await supabase
    .from('produtos')
    .select('id, familia, faturamento_tipo')
    .eq('nome', 'APARELHO')
    .maybeSingle();
  if (!prodAparelho) {
    return { criados: 0, errors: ['Produto APARELHO não encontrado no cadastro de produtos'] };
  }

  const { data: jaTem } = await supabase
    .from('itens_pedido')
    .select('id,ddd,linha,plano_nome,valor_unit,observacoes_item')
    .eq('pedido_id', pedidoId)
    .eq('produto_id', (prodAparelho as { id: string }).id);
  const aparelhosExistentes = (jaTem || []) as Array<{
    id: string; ddd?: string | null; linha?: string | null; plano_nome?: string | null;
    valor_unit?: number | string | null; observacoes_item?: string | null;
  }>;

  let statusInicial: StatusInicial | null = null;
  let seq: number | null = null;

  for (const linha of comAparelho) {
    const ap = linha.aparelho!;
    const numeroProposta = normalizarMsisdn(linha.numero);
    const existente = aparelhosExistentes.find((item) =>
      normalizarMsisdn(`${item.ddd || ''}${item.linha || ''}`) === numeroProposta
      || (item.observacoes_item || '').includes(`vinculado à linha ${linha.numero}`)
    );
    if (existente) {
      const modeloIgual = existente.plano_nome === ap.nome;
      const valorIgual = Math.abs(Number(existente.valor_unit) - Number(ap.preco)) < 0.01;
      if (!modeloIgual || !valorIgual) {
        const { error } = await supabase.rpc('api_sincronizar_aparelho_item_proposta', {
          p_item_id: existente.id,
        });
        if (error) errors.push(`Erro ao sincronizar APARELHO da linha ${linha.numero}: ${error.message}`);
      }
      continue;
    }

    statusInicial ??= await buscarStatusInicial();
    if (!statusInicial) {
      errors.push('Status inicial do pedido não encontrado');
      continue;
    }
    seq ??= await buscarProximoSeq(pedidoId);

    const { ddd, linha: numeroLinha } = extrairDDDLinha(linha.numero);
    const formaTxt = ap.forma === 'avista'
      ? 'à vista (preço base, sem subsídio)'
      : `${ap.forma} de R$ ${(ap.parcela ?? ap.preco / (ap.forma === '24x' ? 24 : 12)).toFixed(2).replace('.', ',')} na fatura`;

    const { error } = await supabase.from('itens_pedido').insert([{
      pedido_id: pedidoId,
      produto_id: (prodAparelho as { id: string }).id,
      quantidade: 1,
      valor_unit: ap.preco,
      plano_nome: ap.nome,
      franquia_gb: null,
      gestor_id: gestorId || null,
      cessionario_id: null,
      tipo_chip: null,
      ddd: ddd || null,
      linha: numeroLinha || null,
      observacoes_item: `Aparelho ${ap.nome} — ${formaTxt} · faixa ${ap.grupoUsado}${ap.faixaGb ? ` ${ap.faixaGb}GB` : ''} · vinculado à linha ${linha.numero}`,
      linha_id: null,
      linha_pai_id: linha.linha_id || null,
      familia: (prodAparelho as { familia: string }).familia,
      faturamento_tipo: (prodAparelho as { faturamento_tipo: string }).faturamento_tipo,
      seq: seq++,
      status_id: statusInicial.id,
      quadro_atual: statusInicial.quadro,
      coluna_atual: statusInicial.coluna,
      entrada_coluna_em: new Date().toISOString(),
    }]);

    if (error) errors.push(`Erro ao criar item APARELHO: ${error.message}`);
    else criados++;
  }

  return { criados, errors };
}

/**
 * PASSAPORT (roaming) que entrou na proposta DEPOIS de o pedido existir.
 *
 * (Douglas 01/09/2026) A revisão só INSERE "linha nova". Passaport não é linha:
 * é um adicional DENTRO de uma linha que já tem item. Resultado: a consultora
 * punha Passaport América na proposta, clicava em criar/aplicar, e o roaming
 * não virava item nenhum — em silêncio, com a proposta mostrando certo. Foi
 * exatamente o que aconteceu com o aparelho (08/08) e o conserto é o mesmo:
 * uma função idempotente que cria o que falta, chamada nos DOIS caminhos.
 * Caso: pedido #P2609010021 (TRADE CONSULTANT), 2 Passaport América perdidos.
 *
 * Idempotente: pula o adicional que já tem item no pedido para aquela linha.
 */
async function criarItensPassaportFaltantes(params: {
  pedidoId: string;
  composicao: ComposicaoProposta;
  gestorId?: string | null;
}): Promise<{ criados: number; errors: string[] }> {
  const { pedidoId, composicao, gestorId } = params;
  const errors: string[] = [];
  let criados = 0;

  const comAdicional = (composicao?.linhas || []).filter(
    (l) => ['PORTABILIDADE', 'TT', 'TT_PRE', 'NOVA', 'LINHA_NOVA', 'RENOVACAO'].includes(l.tipo)
      && Object.values(l.adicionais ?? {}).some((q) => Number(q) > 0),
  );
  if (comAdicional.length === 0) return { criados: 0, errors: [] };

  const ids = comAdicional
    .flatMap((linha) => Object.entries(linha.adicionais ?? {}))
    .filter(([, qtd]) => Number(qtd) > 0)
    .map(([id]) => id);
  const resolvidos = await resolverAdicionaisMovel(ids);
  if (resolvidos.errors.length > 0) return { criados: 0, errors: resolvidos.errors };

  const produtoIds = [...new Set([...resolvidos.adicionais.values()].map((a) => a.produtoId))];
  const { data: itensDoPedido } = await supabase
    .from('itens_pedido')
    .select('id, produto_id, observacoes_item, linha_id, linhas_telefone')
    .eq('pedido_id', pedidoId);
  const itens = (itensDoPedido || []) as Array<{
    produto_id: string | null; observacoes_item?: string | null;
    linha_id?: string | null; linhas_telefone?: unknown;
  }>;

  const statusInicial = await buscarStatusInicial();
  if (!statusInicial) return { criados: 0, errors: ['Status inicial do pedido não encontrado'] };
  let seq = await buscarProximoSeq(pedidoId);

  for (const linha of comAdicional) {
    // O produto de comissão é o do item principal DAQUELA linha (mesma régua da
    // criação: o Passaport comissiona pelo produto da linha que ele acompanha).
    // ⛔ Casar por `linha_id` NÃO basta: o item guarda o linha_id de quando ele
    // nasceu, e a composição gera outro quando a linha é remontada — no pedido
    // #P2609010021 nenhum dos 5 itens batia com a proposta. A régua da casa
    // (linha_id OU telefone) é `linhaPertenceAItem`. (Douglas 01/09/2026)
    const itemPrincipal = itens.find(
      (i) => !produtoIds.includes(i.produto_id || '')
        && linhaPertenceAItem(linha, i as ItemPedidoMatch),
    );
    for (const [adicId, quantidade] of Object.entries(linha.adicionais ?? {})) {
      if (Number(quantidade) <= 0) continue;
      const adicional = resolvidos.adicionais.get(adicId);
      if (!adicional) continue;
      // ⛔ Comparar a FRASE inteira não funciona: os laços que criam o Passaport
      // escrevem três prefixos diferentes — "Vinculado à linha:" (PN/TT),
      // "Vinculado à linha HA:" (bloco/linha nova) e "Vinculado à linha MP:"
      // (renovação). O `includes` da frase de PN nunca casava nos outros dois, a
      // rede achava que faltava e INSERIA DE NOVO: roaming em dobro no pedido de
      // renovação e de bloco. Agora a comparação é pelos DÍGITOS do telefone, que
      // é o mesmo nos três. (Douglas 01/09/2026)
      const telefoneDaObs = (obs?: string | null) => {
        const m = String(obs ?? '').match(/Vinculado à linha(?:\s+MP|\s+HA)?:\s*(.+)/i);
        return m ? m[1].replace(/\D/g, '') : '';
      };
      const digitosDaLinha = String(linha.numero ?? '').replace(/\D/g, '');
      const jaTem = itens.some((i) => {
        if (i.produto_id !== adicional.produtoId) return false;
        const daObs = telefoneDaObs(i.observacoes_item);
        if (daObs && digitosDaLinha) {
          return daObs === digitosDaLinha
            || daObs.endsWith(digitosDaLinha) || digitosDaLinha.endsWith(daObs);
        }
        // linha sem telefone: cai na régua da casa (linha_id ou telefone do item)
        return linhaPertenceAItem(linha, i as ItemPedidoMatch);
      });
      if (jaTem) continue;
      if (!itemPrincipal?.produto_id) {
        errors.push(`Passaport ${adicional.nome} ficou sem o produto principal da linha ${linha.numero}`);
        continue;
      }
      const produto = await buscarProduto(adicional.produtoId);
      if (!produto) {
        errors.push(`Produto PASSAPORT ${adicional.nome} não encontrado`);
        continue;
      }
      const produtoPrincipal = await buscarProduto(itemPrincipal.produto_id);

      const { error } = await supabase.from('itens_pedido').insert([{
        pedido_id: pedidoId,
        produto_id: adicional.produtoId,
        produto_comissao_id: itemPrincipal.produto_id,
        quantidade: Number(quantidade),
        valor_unit: adicional.valor,
        plano_nome: adicional.nome,
        franquia_gb: adicional.gb || null,
        gestor_id: gestorId || null,
        cessionario_id: null,
        tipo_chip: null,
        observacoes_item: `Vinculado à linha: ${linha.numero}`,
        ...camposLinhaPassaporte(linha.numero),
        linha_id: null,
        familia: produto.familia,
        faturamento_tipo: produtoPrincipal?.faturamento_tipo ?? produto.faturamento_tipo,
        seq: seq++,
        status_id: statusInicial.id,
        quadro_atual: statusInicial.quadro,
        coluna_atual: statusInicial.coluna,
        entrada_coluna_em: new Date().toISOString(),
      }]);

      if (error) errors.push(`Erro ao criar item PASSAPORT: ${error.message}`);
      else criados++;
    }
  }

  return { criados, errors };
}

/**
 * PASSAPORT QUE SAIU DA PROPOSTA (Douglas 09/10/2026, pedido #P2610080041).
 *
 * `criarItensPassaportFaltantes` só CRIA. A consultora trocou o Passaport
 * América pelo Mundo, depois tirou o Mundo de uma das linhas: a proposta ficou
 * com 1 Passaport e o pedido com 3, porque nada tirava o que saiu.
 * Devolve os itens de Passaport do pedido que não têm mais par
 * (produto + telefone) na composição atual. Quem chama decide o que fazer.
 */
async function itensPassaportForaDaProposta(params: {
  pedidoId: string;
  composicao: ComposicaoProposta;
}): Promise<{ itens: Array<{ id: string; quadro_atual: string | null; plano_nome: string | null; observacoes_item: string | null }>; errors: string[] }> {
  const { pedidoId, composicao } = params;
  const { data: catalogo, error: errCat } = await supabase
    .from('planos_proposta_catalogo' as never)
    .select('codigo,produto_pedido_id')
    .eq('categoria', 'adicional_movel');
  if (errCat) return { itens: [], errors: [`Não foi possível conferir os Passaports: ${errCat.message}`] };
  const produtoPorCodigo = new Map<string, string>();
  for (const c of (catalogo ?? []) as Array<{ codigo: string; produto_pedido_id: string | null }>) {
    if (c.produto_pedido_id) produtoPorCodigo.set(c.codigo, c.produto_pedido_id);
  }
  const produtosPassaport = new Set(produtoPorCodigo.values());
  if (produtosPassaport.size === 0) return { itens: [], errors: [] };

  const desejados = new Set<string>();
  for (const linha of composicao?.linhas || []) {
    const tel = String(linha.numero ?? '').replace(/\D/g, '');
    for (const [codigo, qtd] of Object.entries(linha.adicionais ?? {})) {
      const prod = produtoPorCodigo.get(codigo);
      if (prod && Number(qtd) > 0) desejados.add(`${prod}|${tel}`);
    }
  }

  const { data, error } = await supabase
    .from('itens_pedido')
    .select('id, produto_id, quadro_atual, plano_nome, observacoes_item')
    .eq('pedido_id', pedidoId)
    .in('produto_id', [...produtosPassaport]);
  if (error) return { itens: [], errors: [`Não foi possível ler os Passaports do pedido: ${error.message}`] };

  const fora = ((data ?? []) as Array<{ id: string; produto_id: string; quadro_atual: string | null; plano_nome: string | null; observacoes_item: string | null }>)
    .filter((i) => {
      const m = String(i.observacoes_item ?? '').match(/Vinculado à linha(?:\s+MP|\s+HA)?:\s*(.+)/i);
      const tel = m ? m[1].replace(/\D/g, '') : '';
      if (!tel) return false;
      return ![...desejados].some((chave) => {
        const [prod, telDesejado] = chave.split('|');
        return prod === i.produto_id && !!telDesejado
          && (tel === telDesejado || tel.endsWith(telDesejado) || telDesejado.endsWith(tel));
      });
    })
    .map(({ produto_id: _p, ...resto }) => resto);
  return { itens: fora, errors: [] };
}

/**
 * Valor da linha JA COM OS ADICIONAIS (Mesh, Claro Fixo, ponto de TV...).
 *
 * (Eduarda 21/08/2026) A criacao do pedido sempre somou os adicionais ao valor
 * do item — a REVISAO nao: ela re-precificava com `plano.valor`, o plano puro, e
 * o adicional evaporava. Caso relatado: MARQUES REPRESENTACOES, proposta com
 * Fibra 600MB +2Mesh e TV +1 ponto batia R$ 234,70 e o pedido ficava R$ 204,70 —
 * exatamente os R$ 30 dos adicionais. A supervisora lia como "recuperou mas o
 * pedido ficou igual".
 *
 * Uma funcao so' para os dois caminhos: com duas contas, elas divergem de novo.
 */
/**
 * (Douglas 06/10/2026) A Fibra 600 dos 3 primeiros meses vende R$ 29,99 na
 * proposta, mas o pedido grava a mensalidade da 600 convergente (R$ 89,90),
 * tenha ou nao movel junto. So' o valor gravado muda; plano e proposta ficam.
 */
const FIBRA_ENTRADA_PROMO = 'fibra-600-promo3m';
const FIBRA_MENSALIDADE_GRAVADA = 'fibra-600-convergente';

function valorBaseGravado(linha: LinhaComposicao, plano?: PlanoCNPJ | null): number {
  const base = plano?.valor ?? linha.plano?.valor ?? 0;
  const id = plano?.id ?? linha.plano?.id ?? linha.planoId;
  if (id !== FIBRA_ENTRADA_PROMO) return base;
  return getPlanoById(FIBRA_MENSALIDADE_GRAVADA, 'fibra')?.valor ?? 89.9;
}

function valorLinhaComAdicionais(linha: LinhaComposicao, plano?: PlanoCNPJ | null): number {
  const base = valorBaseGravado(linha, plano);
  if (!linha.adicionais) return base;
  let total = base;
  for (const [adicId, qtd] of Object.entries(linha.adicionais)) {
    if (!qtd || qtd <= 0) continue;
    const a = ADICIONAIS_FIBRA.find((x) => x.id === adicId)
           || ADICIONAIS_TV.find((x) => x.id === adicId);
    if (a) total += a.valor * qtd;
  }
  return total;
}

/**
 * Produto do pedido de UMA linha, na mesma cadeia que o conversor usa:
 * 1) o produto da OPCAO do catalogo, 2) o default da categoria custom,
 * 3) o mapeamento por TIPO (`proposta_produto_mapeamento`).
 *
 * (Eduarda 21/08/2026) Existe porque a REVISAO so' sabia o passo 1 — mandava
 * `plano.produtoPedidoId` e, quando ele era nulo, `null` (= mantem o produto
 * atual). E as categorias `fibra` (6 opcoes) e `tv` (4) NAO TEM produto no
 * catalogo: nenhuma delas. Resultado: trocar Fibra CNPJ <-> Fibra TT numa
 * revisao nunca mexia no produto do item — ele continuava o antigo para sempre.
 */
async function resolverProdutoDaLinha(linha: LinhaComposicao): Promise<string | null> {
  // ⛔ TT e' MODALIDADE COMERCIAL, nao familia de plano — e por isso ganha dos
  // dois primeiros passos. TT FIXA nao fatura (`faturamento_tipo='NAO PAGO'`);
  // FIBRA e FIBRA NEXT faturam como VENDA NOVA e contam `qtd_fibra`. Os passos 1 e 2
  // respondem "que plano e' esse" (Fibra Next 800MB), nunca "como isso e' vendido".
  //
  // Como o `tipoFibra` era o 3o da fila, so' era alcancado quando a linha nao tinha
  // plano NEM categoria custom: fibra simples marcada TT saia TT FIXA (certo) e
  // Fibra Next marcada TT saia FIBRA NEXT faturando (errado) — o botao TT do montador
  // virava enfeite. Mesma regra ja escrita em `planosCNPJ.ts` ("TT (TT FIXA)") e em
  // `familiasProduto.ts` ("FIBRA com tipoFibra='TT' -> TT FIXA").
  // (Caso LPM CONSTRUTORA, pedido 2609020046 — Douglas 02/09/2026)
  if (linha.tipo === 'FIBRA' && linha.tipoFibra === 'TT') {
    const ttFixa = await resolverProdutoId('FIBRA', 'TT');
    if (ttFixa) return ttFixa;
  }
  return (
    linha.plano?.produtoPedidoId
    || (linha.categoriaCustom ? await buscarProdutoPorCategoria(linha.categoriaCustom, linha.planoId) : null)
    || await resolverProdutoId(linha.tipo, linha.tipoFibra, linha.tipoBloco)
  );
}

async function converterPropostaEmItens({
  pedidoId,
  composicao,
  gestorId,
  cessionarioId,
}: ConverterPropostaParams): Promise<ConverterPropostaResult> {
  const errors: string[] = [];
  let itensCreados = 0;

  if (!composicao.linhas || composicao.linhas.length === 0) {
    return { success: true, itensCreados: 0, errors: [] };
  }

  // 🛡️ DEDUP (25/06): o mesmo telefone NAO pode aparecer 2x na composicao. O bug de merge
  // da revisao gerava telefone duplicado com planos diferentes (ex: ren-11 + ren-70) ->
  // item e preco em DOBRO. Mantem a PRIMEIRA ocorrencia por telefone; linhas sem telefone
  // (bloco/fibra/servico) passam todas.
  const _telVistos = new Set<string>();
  const _linhasUnicas = composicao.linhas.filter((l: LinhaComposicao) => {
    const tel = String(l?.numero ?? '').replace(/\D/g, '');
    if (tel.length < 8) return true;
    if (_telVistos.has(tel)) { console.warn('[CONVERTER] telefone duplicado ignorado:', tel); return false; }
    _telVistos.add(tel);
    return true;
  });

  const idsAdicionaisMovel = _linhasUnicas
    .filter((linha) => ['PORTABILIDADE', 'TT', 'TT_PRE', 'NOVA', 'LINHA_NOVA', 'RENOVACAO'].includes(linha.tipo))
    .flatMap((linha) => Object.entries(linha.adicionais ?? {}))
    .filter(([, quantidade]) => quantidade > 0)
    .map(([id]) => id);
  const passaportsResolvidos = await resolverAdicionaisMovel(idsAdicionaisMovel);
  // ⛔ PROBLEMA DO PASSAPORT NÃO DERRUBA A PROPOSTA INTEIRA (Douglas 02/09/2026).
  // Antes, qualquer erro aqui — código fora do catálogo, opção sem produto
  // vinculado, ou até uma falha passageira do SELECT — abortava a conversão
  // ANTES do primeiro item: o pedido nascia vazio, o consultor via um toast
  // falando de "Passaport" e nenhuma linha PN/TT/Fibra virava item. O roaming é
  // um acompanhamento; ele não pode levar a venda junto. Os erros continuam
  // aparecendo no toast, e o laço já pula sozinho o adicional que não resolveu.
  if (passaportsResolvidos.errors.length > 0) {
    console.warn('[CONVERTER] Passaport com problema — sigo criando o resto:', passaportsResolvidos.errors);
    errors.push(...passaportsResolvidos.errors);
  }

  // Buscar status inicial
  const statusInicial = await buscarStatusInicial();
  if (!statusInicial) {
    return { success: false, itensCreados: 0, errors: ['Status inicial não encontrado'] };
  }

  // Buscar próximo seq
  let proximoSeq = await buscarProximoSeq(pedidoId);

  try {
    // Separar linhas por tipo
    const linhasPNTT = _linhasUnicas.filter(
      l => l.tipo === 'PORTABILIDADE' || l.tipo === 'TT' || l.tipo === 'TT_PRE'
    );
    const linhasHA = _linhasUnicas.filter(
      l => l.tipo === 'NOVA' || l.tipo === 'LINHA_NOVA'
    );
    const linhasMP = _linhasUnicas.filter(l => l.tipo === 'RENOVACAO');
    const linhasFIBRA = _linhasUnicas.filter(l => l.tipo === 'FIBRA');
    const linhasTV = _linhasUnicas.filter(l => l.tipo === 'TV');
    const produtoComissaoPorLinha = new Map<LinhaComposicao, string>();
    // SERVICO = legado/categoria custom sem comportamento próprio (kind='servico').
    // NUNCA descartar em silêncio: vira 1 item por linha via produto da opção do catálogo.
    const linhasSERVICO = _linhasUnicas.filter(
      l => l.tipo === 'SERVICO' && l.categoriaCustom !== 'aparelhos'
    );

    // 1. PN e TT: 1 item por linha
    console.log(`📋 [CONVERTER] Processando ${linhasPNTT.length} linhas PN/TT (PORTABILIDADE/TT)...`);
    for (const linha of linhasPNTT) {
      try {
        console.log('📝 [CONVERTER] PORTABILIDADE/TT - Detalhes completos:', {
          tipo: linha.tipo,
          planoId: linha.planoId,
          numero: linha.numero,
          planoObjeto: linha.plano ? { nome: linha.plano.nome, valor: linha.plano.valor } : 'não hidratado'
        });

        // Produto: 1º o da OPÇÃO (produtoPedidoId — ex.: pn_incremento → PN INCREMENTO),
        // 2º o da categoria custom no catálogo, 3º mapeamento por tipo
        // (PORTABILIDADE→PN NOVO; TT/TT_PRE→mapeamento direto).
        const produtoId = linha.plano?.produtoPedidoId
          || (linha.categoriaCustom ? await buscarProdutoPorCategoria(linha.categoriaCustom, linha.planoId) : null)
          || await resolverProdutoId(linha.tipo);

        if (!produtoId) {
          const errMsg = `Produto não encontrado para tipo ${linha.tipo}`;
          console.error('❌ [CONVERTER] ' + errMsg);
          errors.push(errMsg);
          continue;
        }
        console.log('✅ [CONVERTER] ProdutoId resolvido:', produtoId);

        const produto = await buscarProduto(produtoId);
        if (!produto) {
          console.error('❌ [CONVERTER] Dados do produto não encontrados:', produtoId);
          errors.push(`Dados do produto ${produtoId} não encontrados`);
          continue;
        }

        const { ddd, linha: numeroLinha } = extrairDDDLinha(linha.numero);
        // Em HA/PN o chip nasce VAZIO: o consultor escolhe ESIM - REAL ou ESIM - SIMCARD no pedido,
        // e o fechamento não passa sem isso (Douglas 22/09/2026). Antes vinha
        // 'e-sim' fixo — o "classifica depois" que parava o ciclo de Qualidade.
        const tipoChip = linhaTemChip(linha) && !linhaExigeTipoChip(linha) ? 'e-sim' : null;

        // ✅ Resolver plano pelo ID
        const plano = resolverPlano(linha);

        const linhasTelefone: LinhaItemJson[] = [
          {
            ddd,
            linha: numeroLinha,
            gb: plano?.gb?.toString() || '',
            operadora_doadora: null, // API busca depois
            tipo_chip: tipoChip,
          },
        ];

        // linha_id opcional — necessário apenas para merge/regenerar, não para criação inicial
        if (!linha.linha_id) {
          console.warn(`⚠️ [CONVERTER] linha_id ausente para PN/TT ${linha.numero} — item criado sem guardrail de merge`);
        }

        const { error } = await supabase.from('itens_pedido').insert([{
          pedido_id: pedidoId,
          produto_id: produtoId,
          quantidade: 1,
          valor_unit: plano?.valor || 0,
          plano_nome: plano?.nome || '',
          franquia_gb: plano?.gb || null,
          gestor_id: gestorId || null,
          cessionario_id: cessionarioId ?? null, // ✅ NUNCA usar gestorId como fallback (FK diferente)
          tipo_chip: tipoChip,
          ddd: ddd || null,
          linha: numeroLinha || null,
          linhas_telefone: linhasTelefone as unknown as Json,
          observacoes_item: [formatarAdicionais(linha.adicionais, true), obsDependente(linha, _linhasUnicas)].filter(Boolean).join(' | '),
          linha_id: linha.linha_id, // ✅ (F) Guardrail para merge no regenerar
          // Campos obrigatórios
          familia: produto.familia,
          faturamento_tipo: produto.faturamento_tipo,
          seq: proximoSeq++,
          status_id: statusInicial.id,
          quadro_atual: statusInicial.quadro,
          coluna_atual: statusInicial.coluna,
          entrada_coluna_em: new Date().toISOString(),
        }]);

        if (error) {
          console.error('❌ [CONVERTER] Erro ao inserir item PN/TT:', error);
          errors.push(`Erro ao criar item PN/TT: ${error.message}`);
        } else {
          produtoComissaoPorLinha.set(linha, produtoId);
          console.log('✅ [CONVERTER] Item PN/TT criado com sucesso');
          itensCreados++;
        }
      } catch (err) {
        console.error('❌ [CONVERTER] Exceção ao criar item PN/TT:', err);
        errors.push(`Exceção ao criar item PN/TT: ${err}`);
      }
    }

    // 1.5 PASSAPORT: Criar itens complementares para adicionais móveis (PN/TT)
    console.log('📋 [CONVERTER] Processando adicionais PASSAPORT para PN/TT...');
    for (const linha of linhasPNTT) {
      if (!linha.adicionais) continue;

      for (const [adicId, quantidade] of Object.entries(linha.adicionais)) {
        if (quantidade <= 0) continue;

        const adicionalMovel = passaportsResolvidos.adicionais.get(adicId);
        if (!adicionalMovel) continue;
        const produtoComissaoId = produtoComissaoPorLinha.get(linha);
        if (!produtoComissaoId) {
          errors.push(`Passaport ${adicionalMovel.nome} ficou sem o produto principal da linha ${linha.numero}`);
          continue;
        }
        const produto = await buscarProduto(adicionalMovel.produtoId);
        if (!produto) {
          console.error(`❌ [CONVERTER] Produto PASSAPORT ${adicionalMovel.nome} não encontrado`);
          errors.push(`Produto PASSAPORT ${adicionalMovel.nome} não encontrado`);
          continue;
        }
        const produtoPrincipal = await buscarProduto(produtoComissaoId);

        const { error } = await supabase.from('itens_pedido').insert([{
          pedido_id: pedidoId,
          produto_id: adicionalMovel.produtoId,
          produto_comissao_id: produtoComissaoId,
          quantidade: quantidade,
          valor_unit: adicionalMovel.valor,
          plano_nome: adicionalMovel.nome,
          franquia_gb: adicionalMovel.gb || null,
          gestor_id: gestorId || null,
          cessionario_id: null,
          tipo_chip: null,
          observacoes_item: `Vinculado à linha: ${linha.numero}`,
          ...camposLinhaPassaporte(linha.numero),
          linha_id: null, // módulo vinculado à linha principal; MSISDN preservado acima
          familia: produto.familia,
          faturamento_tipo: produtoPrincipal?.faturamento_tipo ?? produto.faturamento_tipo,
          seq: proximoSeq++,
          status_id: statusInicial.id,
          quadro_atual: statusInicial.quadro,
          coluna_atual: statusInicial.coluna,
          entrada_coluna_em: new Date().toISOString(),
        }]);

        if (error) {
          console.error(`❌ [CONVERTER] Erro ao criar item PASSAPORT: ${error.message}`);
          errors.push(`Erro ao criar item PASSAPORT: ${error.message}`);
        } else {
          console.log(`✅ [CONVERTER] Item PASSAPORT ${adicionalMovel.nome} criado para linha ${linha.numero}`);
          itensCreados++;
        }
      }
    }

    // 2. HA (Linha Nova/Bloco): Agrupar por plano
    if (linhasHA.length > 0) {
      console.log(`📋 [CONVERTER] Processando ${linhasHA.length} linhas HA (Nova)...`);
      const gruposHA = agruparPorPlano(linhasHA);

      for (const [planoKey, linhasDoPlano] of Object.entries(gruposHA)) {
        console.log(`📝 [CONVERTER] Grupo HA: ${planoKey} com ${linhasDoPlano.length} linhas`);
        try {
          // tipoBloco do grupo (todas as linhas do grupo têm o mesmo, pelo agrupamento)
          const tipoBlocoGrupo = linhasDoPlano[0].tipoBloco || 'NOVO';
          // Produto: 1º o da OPÇÃO do catálogo (mesmo produto do mapeamento nos builtin),
          // 2º categoria custom, 3º mapeamento por tipoBloco.
          const produtoId = linhasDoPlano[0].plano?.produtoPedidoId
            || (linhasDoPlano[0].categoriaCustom ? await buscarProdutoPorCategoria(linhasDoPlano[0].categoriaCustom, linhasDoPlano[0].planoId) : null)
            || await resolverProdutoId('NOVA', undefined, tipoBlocoGrupo);

          if (!produtoId) {
            errors.push('Produto não encontrado para tipo NOVA/LINHA_NOVA');
            continue;
          }

          const produto = await buscarProduto(produtoId);
          if (!produto) {
            errors.push(`Dados do produto ${produtoId} não encontrados`);
            continue;
          }

            // ✅ Resolver plano pelo ID
          const planoHA = resolverPlano(linhasDoPlano[0]);

          // Determinar prefixo baseado no tipo original das linhas (Bloco de Dados ou Linha Nova)
          const tipoOriginal = linhasDoPlano[0].tipo;
          let prefixoTipo: string;
          if (tipoOriginal === 'NOVA') {
            // Diferenciar Bloco Novo / Incremento / Renovação no nome do item
            if (tipoBlocoGrupo === 'INCREMENTO') prefixoTipo = 'Bloco de Dados (Incremento)';
            else if (tipoBlocoGrupo === 'RENOVACAO') prefixoTipo = 'Bloco de Dados (Renovação)';
            else prefixoTipo = 'Bloco de Dados (Novo)';
          } else {
            prefixoTipo = 'Linha Nova';
          }

          // Mesma régua do resto: em HA/PN nasce vazio e o consultor escolhe.
          const chipDaLinha = (l: LinhaComposicao) =>
            linhaTemChip(l) && !linhaExigeTipoChip(l) ? 'e-sim' : null;
          const linhasTelefone: LinhaItemJson[] = linhasDoPlano.map(l => {
            const p = resolverPlano(l);
            const _dddNova = String(l.numero ?? '').replace(/\D/g, '');
            return {
              ddd: _dddNova.length === 2 ? _dddNova : '',
              linha: 'Número Novo',
              gb: p?.gb?.toString() || '',
              operadora_doadora: null,
              tipo_chip: chipDaLinha(l),
            };
          });

          const { error } = await supabase.from('itens_pedido').insert([{
            pedido_id: pedidoId,
            produto_id: produtoId,
            quantidade: linhasDoPlano.length,
            valor_unit: planoHA?.valor || 0,
            plano_nome: planoHA ? `${prefixoTipo} - ${planoHA.nome}` : prefixoTipo,
            franquia_gb: planoHA?.gb || null,
            gestor_id: gestorId || null,
            cessionario_id: null,
            tipo_chip: chipDaLinha(linhasDoPlano[0]),
            // ✅ Espelhar nos escalares para não travar na validação SQL
            // (que olha itens_pedido.ddd / .linha e ignora linhas_telefone JSONB).
            // Linha Nova / Bloco de Dados nasce sem número definido.
            ddd: (() => { const d = String(linhasDoPlano[0]?.numero ?? '').replace(/\D/g, ''); return d.length === 2 ? d : null; })(),
            linha: 'NÚMERO NOVO',
            linhas_telefone: linhasTelefone as unknown as Json,
            observacoes_item: [formatarAdicionais(linhasDoPlano[0].adicionais, true), obsDependente(linhasDoPlano[0], _linhasUnicas)].filter(Boolean).join(' | '),
            linha_id: linhasDoPlano[0].linha_id || null, // ✅ (F) Agrupamento HA - usar primeira linha_id
            // Campos obrigatórios
            familia: produto.familia,
            faturamento_tipo: produto.faturamento_tipo,
            seq: proximoSeq++,
            status_id: statusInicial.id,
            quadro_atual: statusInicial.quadro,
            coluna_atual: statusInicial.coluna,
            entrada_coluna_em: new Date().toISOString(),
          }]);

          if (error) {
            errors.push(`Erro ao criar item HA: ${error.message}`);
          } else {
            linhasDoPlano.forEach((linha) => produtoComissaoPorLinha.set(linha, produtoId));
            itensCreados++;
          }
        } catch (err) {
          errors.push(`Exceção ao criar item HA: ${err}`);
        }
      }
    }

    // 2.5 PASSAPORT: Criar itens complementares para adicionais móveis (HA)
    console.log('📋 [CONVERTER] Processando adicionais PASSAPORT para HA...');
    for (const linha of linhasHA) {
      if (!linha.adicionais) continue;

      for (const [adicId, quantidade] of Object.entries(linha.adicionais)) {
        if (quantidade <= 0) continue;

        const adicionalMovel = passaportsResolvidos.adicionais.get(adicId);
        if (!adicionalMovel) continue;
        const produtoComissaoId = produtoComissaoPorLinha.get(linha);
        if (!produtoComissaoId) {
          errors.push(`Passaport ${adicionalMovel.nome} ficou sem o produto principal da linha ${linha.numero}`);
          continue;
        }
        const produto = await buscarProduto(adicionalMovel.produtoId);
        if (!produto) {
          errors.push(`Produto PASSAPORT ${adicionalMovel.nome} não encontrado`);
          continue;
        }
        const produtoPrincipal = await buscarProduto(produtoComissaoId);

        const { error } = await supabase.from('itens_pedido').insert([{
          pedido_id: pedidoId,
          produto_id: adicionalMovel.produtoId,
          produto_comissao_id: produtoComissaoId,
          quantidade: quantidade,
          valor_unit: adicionalMovel.valor,
          plano_nome: adicionalMovel.nome,
          franquia_gb: adicionalMovel.gb || null,
          gestor_id: gestorId || null,
          cessionario_id: null,
          tipo_chip: null,
          observacoes_item: `Vinculado à linha HA: ${linha.numero}`,
          ...camposLinhaPassaporte(linha.numero),
          linha_id: null, // módulo vinculado à linha principal; MSISDN preservado acima
          familia: produto.familia,
          faturamento_tipo: produtoPrincipal?.faturamento_tipo ?? produto.faturamento_tipo,
          seq: proximoSeq++,
          status_id: statusInicial.id,
          quadro_atual: statusInicial.quadro,
          coluna_atual: statusInicial.coluna,
          entrada_coluna_em: new Date().toISOString(),
        }]);

        if (error) {
          errors.push(`Erro ao criar item PASSAPORT: ${error.message}`);
        } else {
          itensCreados++;
        }
      }
    }

    // 3. MP (Renovação): cada linha renovada vira um item próprio
    // ⛔ NO CPF NÃO EXISTE SNAPSHOT, TM NEM FAIXA (Douglas 17/09 e 25/09/2026):
    // o banco não gera renovacao_snapshot para PF, e exigi-lo aqui fazia toda
    // renovação CPF nascer sem item. O item sai da linha da proposta: número,
    // plano do catálogo CPF, produto vinculado a ele e valor.
    const renovacaoCpf = composicao.tipoPessoa === 'PF'
      || linhasMP.some((linha) => linha.plano?.tipoPessoa === 'PF');
    if (linhasMP.length > 0 && renovacaoCpf) {
      for (const linhaMP of linhasMP) {
        try {
          const produtoId = linhaMP.plano?.produtoPedidoId;
          if (!produtoId) {
            errors.push(`O plano CPF ${linhaMP.plano?.nome || linhaMP.planoId || ''} precisa de vínculo com um produto CPF`);
            continue;
          }
          const produto = await buscarProduto(produtoId);
          if (!produto) {
            errors.push(`Dados do produto ${produtoId} não encontrados`);
            continue;
          }
          const telefone = extrairDDDLinha(linhaMP.numero || '');
          const valor = Number(linhaMP.ticketNovo ?? linhaMP.receitaRenovada ?? linhaMP.plano?.valor ?? 0);
          // Dependente e plano 0GB vêm com gb=0 do catálogo; a trava do banco só aceita
          // vazio ou maior que zero, e o 0 abortava o pedido inteiro (Douglas 30/09/2026).
          const gb = Number(linhaMP.plano?.gb) > 0 ? Number(linhaMP.plano?.gb) : null;
          const { error } = await supabase.from('itens_pedido').insert([{
            pedido_id: pedidoId,
            produto_id: produtoId,
            quantidade: 1,
            valor_unit: valor,
            plano_nome: linhaMP.plano?.nome || linhaMP.planoNovo || 'Renovação CPF',
            franquia_gb: gb,
            gestor_id: gestorId || null,
            cessionario_id: null,
            tipo_chip: null,
            ddd: telefone.ddd || null,
            linha: telefone.linha || null,
            linhas_telefone: [{
              ddd: telefone.ddd,
              linha: telefone.linha,
              gb: gb === null ? '' : String(gb),
              plano_novo: linhaMP.plano?.nome || null,
              valor_novo: String(valor),
              operadora_doadora: null,
              tipo_chip: null,
            }] as unknown as Json,
            observacoes_item: [`RENOVAÇÃO CPF · R$ ${valor.toFixed(2)}`, obsDependente(linhaMP, _linhasUnicas)].filter(Boolean).join(' | '),
            linha_id: linhaMP.linha_id || null,
            familia: produto.familia,
            faturamento_tipo: produto.faturamento_tipo,
            seq: proximoSeq++,
            status_id: statusInicial.id,
            quadro_atual: statusInicial.quadro,
            coluna_atual: statusInicial.coluna,
            entrada_coluna_em: new Date().toISOString(),
          }]);
          if (error) {
            errors.push(`Erro ao criar item de renovação CPF: ${error.message}`);
          } else {
            produtoComissaoPorLinha.set(linhaMP, produtoId);
            itensCreados++;
          }
        } catch (err) {
          errors.push(`Exceção ao criar item de renovação CPF: ${err}`);
        }
      }
    } else if (linhasMP.length > 0) {
      try {
        const produtoId = linhasMP[0].plano?.produtoPedidoId
          || (linhasMP[0].categoriaCustom ? await buscarProdutoPorCategoria(linhasMP[0].categoriaCustom, linhasMP[0].planoId) : null)
          || await resolverProdutoId('RENOVACAO');

        if (!produtoId) {
          errors.push('Produto não encontrado para tipo RENOVACAO');
        } else {
          const produto = await buscarProduto(produtoId);
          if (!produto) {
            errors.push(`Dados do produto ${produtoId} não encontrados`);
          } else {
            const { data: pedidoSnapshot } = await supabase
              .from('pedidos')
              .select('lead_origem_id')
              .eq('id', pedidoId)
              .maybeSingle();
            let snapshotQuery = supabase
              .from('renovacao_snapshot' as never)
              .select('payload')
              .order('created_at', { ascending: false })
              .limit(1);
            snapshotQuery = pedidoSnapshot?.lead_origem_id
              ? snapshotQuery.or(`pedido_id.eq.${pedidoId},lead_id.eq.${pedidoSnapshot.lead_origem_id}`)
              : snapshotQuery.eq('pedido_id', pedidoId);
            const { data: snapshotData } = await snapshotQuery.maybeSingle();
            const payload = (snapshotData as unknown as { payload?: RenovacaoSnapshotPayload } | null)?.payload;
            const detalhes = Array.isArray(payload?.linhas) ? payload.linhas : [];
            const porNumero = new Map(linhasMP.map((linha) => [normalizarMsisdn(linha.numero), linha]));

            if (detalhes.length === 0) {
              errors.push('Snapshot por linha da renovação não foi encontrado');
            }

            for (const detalhe of detalhes) {
              const composicaoLinha = porNumero.get(normalizarMsisdn(detalhe.msisdn)) || linhasMP[0];
              const telefone = extrairDDDLinha(detalhe.msisdn || composicaoLinha.numero || '');
              const deltaAtivo = payload?.deltaFormulaSign === 'LITERAL_REGULAMENTO'
                ? detalhe.delta_literal
                : detalhe.delta_economico;
              const linhaTelefone = {
                ddd: telefone.ddd,
                linha: telefone.linha,
                gb: detalhe.gb_novo === null ? '' : String(detalhe.gb_novo),
                delta: deltaAtivo === null ? null : String(deltaAtivo),
                delta_economico: detalhe.delta_economico === null ? null : String(detalhe.delta_economico),
                delta_literal: detalhe.delta_literal === null ? null : String(detalhe.delta_literal),
                delta_ativo: deltaAtivo === null ? null : String(deltaAtivo),
                delta_formula_sign: payload?.deltaFormulaSign || 'ECONOMICO',
                faixa: detalhe.faixa_apurada === null ? null : String(detalhe.faixa_apurada),
                tabela: detalhe.tabela_apurada,
                plano_atual: detalhe.plano_atual,
                plano_novo: detalhe.plano_novo,
                valor_atual: String(detalhe.ticket_atual),
                valor_novo: String(detalhe.receita_renovada),
                ticket_atual: String(detalhe.ticket_atual),
                ticket_novo: String(detalhe.ticket_novo),
                receita_adicionada: String(detalhe.receita_adicionada || 0),
                operadora_doadora: null,
                tipo_chip: null,
              };
              const totalValor = Number(detalhe.ticket_novo || 0);

              const { error } = await supabase.from('itens_pedido').insert([{
                pedido_id: pedidoId,
                produto_id: produtoId,
                quantidade: 1,
                valor_unit: totalValor,
                plano_nome: `Renovação ${detalhe.msisdn}`,
                franquia_gb: detalhe.gb_novo || null,
                gestor_id: gestorId || null,
                cessionario_id: null,
                tipo_chip: null,
                ddd: telefone.ddd || null,
                linha: telefone.linha || null,
                linhas_telefone: [linhaTelefone] as unknown as Json,
                observacoes_item: `RENOVAÇÃO POR LINHA · R$ ${totalValor.toFixed(2)}`,
                linha_id: composicaoLinha.linha_id || null,
                familia: produto.familia,
                faturamento_tipo: produto.faturamento_tipo,
                seq: proximoSeq++,
                status_id: statusInicial.id,
                quadro_atual: statusInicial.quadro,
                coluna_atual: statusInicial.coluna,
                entrada_coluna_em: new Date().toISOString(),
              }]);

              if (error) {
                errors.push(`Erro ao criar item de renovação: ${error.message}`);
              } else {
                produtoComissaoPorLinha.set(composicaoLinha, produtoId);
                itensCreados++;
              }
            }
          }
        }
      } catch (err) {
        errors.push(`Exceção ao criar item MP: ${err}`);
      }
    }

    // 3.5 PASSAPORT: Criar itens complementares para adicionais móveis (MP/Renovação)
    console.log('📋 [CONVERTER] Processando adicionais PASSAPORT para MP...');
    for (const linha of linhasMP) {
      if (!linha.adicionais) continue;

      for (const [adicId, quantidade] of Object.entries(linha.adicionais)) {
        if (quantidade <= 0) continue;

        const adicionalMovel = passaportsResolvidos.adicionais.get(adicId);
        if (!adicionalMovel) continue;
        const produtoComissaoId = produtoComissaoPorLinha.get(linha);
        if (!produtoComissaoId) {
          errors.push(`Passaport ${adicionalMovel.nome} ficou sem o produto principal da linha ${linha.numero}`);
          continue;
        }
        const produto = await buscarProduto(adicionalMovel.produtoId);
        if (!produto) {
          errors.push(`Produto PASSAPORT ${adicionalMovel.nome} não encontrado`);
          continue;
        }
        const produtoPrincipal = await buscarProduto(produtoComissaoId);

        const { error } = await supabase.from('itens_pedido').insert([{
          pedido_id: pedidoId,
          produto_id: adicionalMovel.produtoId,
          produto_comissao_id: produtoComissaoId,
          quantidade: quantidade,
          valor_unit: adicionalMovel.valor,
          plano_nome: adicionalMovel.nome,
          franquia_gb: adicionalMovel.gb || null,
          gestor_id: gestorId || null,
          cessionario_id: null,
          tipo_chip: null,
          observacoes_item: `Vinculado à linha MP: ${linha.numero}`,
          ...camposLinhaPassaporte(linha.numero),
          linha_id: null, // módulo vinculado à linha principal; MSISDN preservado acima
          familia: produto.familia,
          faturamento_tipo: produtoPrincipal?.faturamento_tipo ?? produto.faturamento_tipo,
          seq: proximoSeq++,
          status_id: statusInicial.id,
          quadro_atual: statusInicial.quadro,
          coluna_atual: statusInicial.coluna,
          entrada_coluna_em: new Date().toISOString(),
        }]);

        if (error) {
          errors.push(`Erro ao criar item PASSAPORT: ${error.message}`);
        } else {
          itensCreados++;
        }
      }
    }

    // 3.7 APARELHO: mesma funcao que a revisao usa — regua unica. Ver
    // criarItensAparelhoFaltantes acima. (Douglas 08/08, unificado em 21/08)
    {
      const rAp = await criarItensAparelhoFaltantes({ pedidoId, composicao, gestorId });
      itensCreados += rAp.criados;
      errors.push(...rAp.errors);
      if (rAp.criados > 0) console.log(`✅ [CONVERTER] ${rAp.criados} item(ns) APARELHO criado(s)`);
    }

    // REDE DO PASSAPORT: o laço de cima já cria o roaming linha a linha, mas ele
    // depende de a linha ter passado pela criação do item principal na MESMA
    // rodada. Esta chamada confere no banco o que ficou de fora e cria só isso —
    // é idempotente (pula o que já existe), então serve de rede tanto aqui
    // quanto na revisão. Roaming não pode ficar para trás em silêncio de novo.
    {
      const rPass = await criarItensPassaportFaltantes({ pedidoId, composicao, gestorId });
      itensCreados += rPass.criados;
      errors.push(...rPass.errors);
      if (rPass.criados > 0) console.log(`✅ [CONVERTER] ${rPass.criados} item(ns) PASSAPORT recuperado(s)`);
    }

    // ⛔ As duas rotinas acima inserem por conta própria e têm o CONTADOR DELAS.
    // O `proximoSeq` daqui fica atrasado, e (pedido_id, seq) é único no banco:
    // o próximo insert — FIBRA, TV ou o SERVIÇO DIGITAL — é recusado e o item
    // nunca nasce, com "duplicate key" só no console. Reler o contador custa uma
    // consulta e evita item sumindo em silêncio. (Douglas 01/09/2026)
    proximoSeq = await buscarProximoSeq(pedidoId);

    // 4. FIBRA: 1 item por linha (suporta CNPJ ou TT)
    for (const linha of linhasFIBRA) {
      try {
        // MESMA cadeia da revisao — `resolverProdutoDaLinha`. Era copiada aqui, e a
        // copia foi o que deixou o TT perder para a categoria custom em UM dos dois
        // caminhos. Uma cadeia so': TT ganha, depois opcao do catalogo, depois
        // categoria custom, depois o mapeamento por tipo.
        const produtoId = await resolverProdutoDaLinha(linha);

        if (!produtoId) {
          errors.push(`Produto não encontrado para tipo FIBRA (${linha.tipoFibra || 'CNPJ'})`);
          continue;
        }

        const produto = await buscarProduto(produtoId);
        if (!produto) {
          errors.push(`Dados do produto ${produtoId} não encontrados`);
          continue;
        }

        // ✅ Resolver plano pelo ID
        const planoFibra = resolverPlano(linha);

        const valorFibra = valorLinhaComAdicionais(linha, planoFibra);

        const linhasTelefone: LinhaItemJson[] = [
          {
            ddd: '',
            linha: '',
            gb: planoFibra?.velocidade || '',
            operadora_doadora: null,
            tipo_chip: 'fixo',
          },
        ];

        // linha_id opcional — necessário apenas para merge/regenerar, não para criação inicial
        if (!linha.linha_id) {
          console.warn(`⚠️ [CONVERTER] linha_id ausente para FIBRA — item criado sem guardrail de merge`);
        }

        const { data: criacao, error } = await supabase.rpc('api_criar_fibra_com_mesh', { p_item: {
          pedido_id: pedidoId,
          produto_id: produtoId,
          quantidade: 1,
          ...(linha.enderecoInstalacao ? { endereco_instalacao: linha.enderecoInstalacao } : {}),
          valor_unit: valorFibra,
          plano_nome: planoFibra?.nome || '',
          franquia_gb: null,
          gestor_id: gestorId || null,
          cessionario_id: null,
          tipo_chip: null,
          linhas_telefone: linhasTelefone as unknown as Json,
          observacoes_item: formatarObservacaoFibra(planoFibra, linha.adicionais, linhasPNTT.length > 0 || linhasHA.length > 0),
          linha_id: linha.linha_id, // ✅ (F) Guardrail para merge no regenerar
          // Campos obrigatórios
          familia: produto.familia,
          faturamento_tipo: produto.faturamento_tipo,
          seq: proximoSeq++,
          status_id: statusInicial.id,
          quadro_atual: statusInicial.quadro,
          coluna_atual: statusInicial.coluna,
          entrada_coluna_em: new Date().toISOString(),
        }, p_adicionais: linha.adicionais || {} });

        if (error) {
          errors.push(`Erro ao criar item FIBRA: ${error.message}`);
        } else {
          itensCreados += criacao?.ja_existia ? 0 : criacao?.mesh_item_id ? 2 : 1;
        }
      } catch (err) {
        errors.push(`Exceção ao criar item FIBRA: ${err}`);
      }
    }

    proximoSeq = await buscarProximoSeq(pedidoId);

    // 5. TV: 1 item por linha
    for (const linha of linhasTV) {
      try {
        // 1º vínculo POR OPÇÃO, 2º default do TIPO custom (proposta_tipos), 3º mapeamento por tipo.
        const produtoId = linha.plano?.produtoPedidoId
          || (linha.categoriaCustom ? await buscarProdutoPorCategoria(linha.categoriaCustom, linha.planoId) : null)
          || await resolverProdutoId('TV');

        if (!produtoId) {
          errors.push('Produto não encontrado para tipo TV');
          continue;
        }

        const produto = await buscarProduto(produtoId);
        if (!produto) {
          errors.push(`Dados do produto ${produtoId} não encontrados`);
          continue;
        }

        // ✅ Resolver plano pelo ID
        const planoTV = resolverPlano(linha);

        // ✅ CORREÇÃO: Calcular valor total incluindo adicionais (Pontos, Ponto 4K)
        const valorTV = valorLinhaComAdicionais(linha, planoTV);

        const linhasTelefone: LinhaItemJson[] = [
          {
            ddd: '',
            linha: '',
            gb: '',
            operadora_doadora: null,
            tipo_chip: null,
          },
        ];

        // linha_id opcional — necessário apenas para merge/regenerar, não para criação inicial
        if (!linha.linha_id) {
          console.warn(`⚠️ [CONVERTER] linha_id ausente para TV — item criado sem guardrail de merge`);
        }

        const { error } = await supabase.from('itens_pedido').insert([{
          pedido_id: pedidoId,
          produto_id: produtoId,
          quantidade: 1,
          valor_unit: valorTV,
          ...(linha.enderecoInstalacao ? { endereco_instalacao: linha.enderecoInstalacao } : {}),
          plano_nome: planoTV?.nome || '',
          franquia_gb: null,
          gestor_id: gestorId || null,
          cessionario_id: null,
          tipo_chip: null,
          linhas_telefone: linhasTelefone as unknown as Json,
          observacoes_item: formatarAdicionais(linha.adicionais, true),
          linha_id: linha.linha_id, // ✅ (F) Guardrail para merge no regenerar
          // Campos obrigatórios
          familia: produto.familia,
          faturamento_tipo: produto.faturamento_tipo,
          seq: proximoSeq++,
          status_id: statusInicial.id,
          quadro_atual: statusInicial.quadro,
          coluna_atual: statusInicial.coluna,
          entrada_coluna_em: new Date().toISOString(),
        }]);

        if (error) {
          errors.push(`Erro ao criar item TV: ${error.message}`);
        } else {
          itensCreados++;
        }
      } catch (err) {
        errors.push(`Exceção ao criar item TV: ${err}`);
      }
    }

    // 6. SERVICO: cada LICENÇA vira um item independente no pedido.
    //
    // 'SERVICO' não é produto — a linha SEMPRE resolve para um produto real do
    // pedido pelo produto_pedido_id da opção (catálogo) ou do tipo
    // (proposta_tipos). Se não achar, erro explícito (antes era descartada calada).
    //
    // A proposta pode continuar mostrando 5× M365 Business Basic agrupado, mas o
    // pedido precisa de cinco itens com quantidade=1. Assim cada licença pode ser
    // cancelada e tramitada separadamente na Planilha de Pedidos.
    // A LINHA VINCULADA entra na chave do grupo (Douglas 05/08: "ter como
    // adicionar o número da linha que este serviço vai vinculado"). Duas licenças
    // da mesma opção presas a telefones diferentes são itens diferentes no pedido
    // — juntá-las perderia a qual linha cada uma pertence.
    const gruposSERVICO: Record<string, LinhaComposicao[]> = {};
    for (const linha of linhasSERVICO) {
      const vinculo = String(linha.linhaVinculada ?? '').replace(/\D/g, '');
      // A chave do agrupamento inclui a LINHA DONA, não só o telefone: linha sem
      // número (Bloco, Linha Nova, PN novo) tem vínculo vazio, e agrupar por
      // telefone colapsava licenças de linhas diferentes num item só. (01/09/2026)
      const dono = linha.linhaVinculadaId || vinculo;
      const key = `${linha.categoriaCustom ?? ''}::${linha.planoId ?? ''}::${dono}`;
      if (!gruposSERVICO[key]) gruposSERVICO[key] = [];
      gruposSERVICO[key].push(linha);
    }

    for (const linhasDaOpcao of Object.values(gruposSERVICO)) {
      const linha = linhasDaOpcao[0];
      try {
        const plano = resolverPlano(linha);
        // 3ª tentativa (05/08): pelo CÓDIGO do plano. Os outros tipos de linha
        // sempre tiveram um último recurso (`resolverProdutoId(linha.tipo)`);
        // só o ramo de serviço não tinha, e era o único que morria quando a
        // composição salva vinha sem `categoriaCustom` e sem `plano`.
        const produtoId = linha.plano?.produtoPedidoId
          || (linha.categoriaCustom ? await buscarProdutoPorCategoria(linha.categoriaCustom, linha.planoId) : null)
          || (linha.planoId ? await buscarProdutoPorCodigoUnico(linha.planoId) : null);

        if (!produtoId) {
          const errMsg = `Produto do pedido não configurado para "${plano?.nome || linha.categoriaCustom || 'serviço'}" — vincule a opção a um produto em Planos & Preços`;
          console.error('❌ [CONVERTER] ' + errMsg);
          errors.push(errMsg);
          continue;
        }

        const produto = await buscarProduto(produtoId);
        if (!produto) {
          errors.push(`Dados do produto ${produtoId} não encontrados`);
          continue;
        }

        const itensServico = linhasDaOpcao.flatMap((linhaServico) => {
          const qtdLicencas = Math.max(1, Math.floor(Number(linhaServico.quantidade) || 1));
          const vinculo = String(linhaServico.linhaVinculada ?? '').replace(/\D/g, '');
          const { ddd: dddVinculo, linha: numeroVinculo } = vinculo.length >= 10
            ? extrairDDDLinha(vinculo)
            : { ddd: null as string | null, linha: null as string | null };

          // M2M é um serviço único (Douglas 27/10/2026): as N linhas de
          // telemetria viram UM item, com quantidade = linhas e o valor de uma
          // linha, sem listar os números. A quantidade não entra em KPI nenhum
          // (`naoContaQuantidade` em tipo-da-linha).
          if (linhaServico.categoriaCustom === CATEGORIA_M2M) {
            return [{
              pedido_id: pedidoId,
              produto_id: produtoId,
              quantidade: qtdLicencas,
              valor_unit: Number(plano?.valor || 0),
              plano_nome: plano?.nome || '',
              franquia_gb: plano?.gb || null,
              gestor_id: gestorId || null,
              cessionario_id: null,
              tipo_chip: null,
              ddd: null,
              linha: null,
              observacoes_item: formatarAdicionais(linhaServico.adicionais, false),
              linha_id: linhaServico.linha_id || null,
              linha_pai_id: null,
              familia: produto.familia,
              faturamento_tipo: produto.faturamento_tipo,
              seq: proximoSeq++,
              status_id: statusInicial.id,
              quadro_atual: statusInicial.quadro,
              coluna_atual: statusInicial.coluna,
              entrada_coluna_em: new Date().toISOString(),
            }];
          }

          return Array.from({ length: qtdLicencas }, () => ({
            pedido_id: pedidoId,
            produto_id: produtoId,
            quantidade: 1,
            valor_unit: plano?.valor || 0,
            plano_nome: plano?.nome || '',
            franquia_gb: plano?.gb || null,
            gestor_id: gestorId || null,
            cessionario_id: null,
            tipo_chip: null,
            ddd: dddVinculo,
            linha: numeroVinculo,
            observacoes_item: formatarAdicionais(linhaServico.adicionais, false),
            linha_id: linhaServico.linha_id || null,
            linha_pai_id: linhaServico.linhaVinculadaId || null,
            familia: produto.familia,
            faturamento_tipo: produto.faturamento_tipo,
            seq: proximoSeq++,
            status_id: statusInicial.id,
            quadro_atual: statusInicial.quadro,
            coluna_atual: statusInicial.coluna,
            entrada_coluna_em: new Date().toISOString(),
          }));
        });

        const { error } = await supabase.from('itens_pedido').insert(itensServico);

        if (error) {
          errors.push(`Erro ao criar item de serviço: ${error.message}`);
        } else {
          console.log(`✅ [CONVERTER] Serviço "${plano?.nome}" criado em ${itensServico.length} item(ns) independente(s)`);
          itensCreados += itensServico.length;
        }
      } catch (err) {
        errors.push(`Exceção ao criar item de serviço: ${err}`);
      }
    }
  } catch (err) {
    errors.push(`Erro geral: ${err}`);
  }

  // Sucesso = pelo menos 1 item criado OU nenhum erro (proposta vazia é OK)
  const success = itensCreados > 0 || errors.length === 0;

  if (errors.length > 0) {
    console.error('[useConverterPropostaEmItens] Erros durante conversão:', errors);
    console.log(`[useConverterPropostaEmItens] Itens criados apesar dos erros: ${itensCreados}`);
  }

  return { success, itensCreados, errors };
}


return { invalidarCacheConversor, resolverPlano, criarItensAparelhoFaltantes, criarItensPassaportFaltantes, itensPassaportForaDaProposta, valorLinhaComAdicionais, resolverProdutoDaLinha, converterPropostaEmItens };
}
